/**
 * Web Vitals reporting using the native PerformanceObserver API.
 * No external dependencies required.
 *
 * Reports: LCP (Largest Contentful Paint), FID (First Input Delay),
 * CLS (Cumulative Layout Shift), TTFB (Time to First Byte).
 *
 * Currently logs to console. To send to an analytics endpoint,
 * replace the `sendMetric` implementation.
 */

interface WebVitalMetric {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
}

const THRESHOLDS: Record<string, [number, number]> = {
  LCP: [2500, 4000],
  FID: [100, 300],
  CLS: [0.1, 0.25],
  TTFB: [800, 1800],
};

function rate(name: string, value: number): WebVitalMetric['rating'] {
  const [good, poor] = THRESHOLDS[name] || [Infinity, Infinity];
  if (value <= good) return 'good';
  if (value <= poor) return 'needs-improvement';
  return 'poor';
}

function sendMetric(metric: WebVitalMetric): void {
  if (import.meta.env.DEV) {
    console.log(`[WebVital] ${metric.name}: ${metric.value.toFixed(2)} (${metric.rating})`);
  }

  // TODO: Send to analytics endpoint when ready:
  // navigator.sendBeacon('/api/vitals', JSON.stringify(metric));
}

/**
 * Initialize Web Vitals observers. Call once in app entry point.
 * Only runs in production to avoid noisy dev logs.
 */
export function initWebVitals(): void {
  if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

  // LCP
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) {
        sendMetric({ name: 'LCP', value: last.startTime, rating: rate('LCP', last.startTime) });
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {
    // Browser doesn't support this observer type
  }

  // FID
  try {
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as PerformanceEventTiming;
        const value = e.processingStart - e.startTime;
        sendMetric({ name: 'FID', value, rating: rate('FID', value) });
      }
    });
    fidObserver.observe({ type: 'first-input', buffered: true });
  } catch {
    // Browser doesn't support this observer type
  }

  // CLS
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const e = entry as PerformanceEntry & { hadRecentInput?: boolean; value?: number };
        if (!e.hadRecentInput) {
          clsValue += e.value || 0;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report CLS on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        sendMetric({ name: 'CLS', value: clsValue, rating: rate('CLS', clsValue) });
      }
    });
  } catch {
    // Browser doesn't support this observer type
  }

  // TTFB
  try {
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navEntry) {
      const ttfb = navEntry.responseStart - navEntry.requestStart;
      sendMetric({ name: 'TTFB', value: ttfb, rating: rate('TTFB', ttfb) });
    }
  } catch {
    // Not available
  }
}
